import * as vp from 'vopay';
import { useEffect, useState, ChangeEvent } from 'react';
import { useClientData } from '@/providers/ClientData.provider';
import { useDataFetchIndicator } from '@/hooks/';
import useFetchClientBankAccounts from '@/hooks/useApi/useFetchClientBankAccounts';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';

export type BankAccountTransferRows = {
  label: vp.BankAccountTransferLabel;
  id: vp.BankAccountTransferId;
  require: boolean;
  preset: keyof vp.TextFieldPresets | undefined;
  type: 'input' | 'select';
  placeholder: string;
  visible: boolean;
  allowNewOption?: boolean;
  getSelectOptions: () => { [key: string]: string };
  disableFiltering?: () => boolean;
  searchCallback?: (term: string) => void;
  onChangeCallBack?: (event: ChangeEvent<HTMLInputElement>) => void;
  SelectLabel?: () => string | undefined;
};

type Props = {
  sourceBank?: vp.ClientBankAccountsLists;
  onDestBankChange: (BankInfo?: vp.ClientBankAccountsLists) => void;
  destBank?: vp.ClientBankAccountsLists;
  country: string;
  onSourceBankChange: (BankInfo?: vp.ClientBankAccountsLists) => void;
};

export const buildBankAccountTransferRows = ({
  sourceBank,
  onSourceBankChange,
  destBank,
  country,
  onDestBankChange,
}: Props): BankAccountTransferRows[] => {
  const { userSettings } = useUserSettingsProvider();
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const [, , , fetchClientBankAccounts] = useFetchClientBankAccounts({});
  const [contactBankAccounts, setContactBankAccounts] = useState({});
  const [BankAccountsOptions, setBankAccountOptions] = useState({});
  const [shouldInitializeBankList, setShouldInitializeBankList] = useState(true);
  const [originalBankList, setOriginalBankList] = useState({});
  const [recipientContactBankAccounts, setRecipientContactBankAccounts] = useState({});
  const [recipientBankAccountOptions, setRecipientBankAccountOptions] = useState({});
  const [shouldInitializeRecipientBankList, setShouldInitializeRecipientBankList] = useState(true);
  const [originalRecipientBankList, setOriginalRecipientBankList] = useState({});

  const {
    response: { LoginUserName },
  } = useClientData();

  const CADOnly: { [key: string]: string } = {
    CAD: 'CAD',
  };

  useEffect(() => {
    init();
  }, []);

  async function init() {
    handleBankAccountsSearch('');
    handleRecipientBankAccountsSearch('');
  }

  async function handleBankAccountsSearch(query: string) {
    setDataFetchingInfo(true, 'loading some data', 'fullScreen');

    const res: vp.ClientBankAccountsResponse = await fetchClientBankAccounts({
      PortalUser: LoginUserName,
      ClientControlled: true,
      SearchText: query,
      Token: '',
      Limit: 100,
      Timezone: userSettings.timezone,
    });

    setDataFetchingInfo(false);

    if (res.Success) {
      setContactBankAccounts(res.BankAccounts);
    } else {
      setContactBankAccounts({});
    }
  }

  async function handleRecipientBankAccountsSearch(query: string) {
    setDataFetchingInfo(true, 'loading some data', 'fullScreen');

    const res: vp.ClientBankAccountsResponse = await fetchClientBankAccounts({
      PortalUser: LoginUserName,
      ClientControlled: true,
      SearchText: query,
      Token: '',
      Limit: 100,
      Timezone: userSettings.timezone,
    });
    setDataFetchingInfo(false);

    if (res.Success) {
      setRecipientContactBankAccounts(res.BankAccounts);
    } else {
      setRecipientContactBankAccounts({});
    }
  }

  useEffect(() => {
    setBankAccountOptions(buildBankAccountOptions(Object.values(contactBankAccounts)));
  }, [contactBankAccounts]);

  useEffect(() => {
    if (shouldInitializeBankList && Object.keys(BankAccountsOptions).length > 0) {
      setOriginalBankList(BankAccountsOptions);
      setShouldInitializeBankList(false);
    }
  }, [BankAccountsOptions]);

  useEffect(() => {
    setRecipientBankAccountOptions(buildBankAccountOptions(Object.values(recipientContactBankAccounts)));
  }, [recipientContactBankAccounts]);

  useEffect(() => {
    if (shouldInitializeRecipientBankList && Object.keys(recipientBankAccountOptions).length > 0) {
      setOriginalRecipientBankList(recipientBankAccountOptions);
      setShouldInitializeRecipientBankList(false);
    }
  }, [recipientBankAccountOptions]);

  return [
    {
      label: vp.BankAccountTransferLabel.Currency,
      id: vp.BankAccountTransferId.Currency,
      require: true,
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: true,
      getSelectOptions: () => (country === 'US' ? vp.US_CURRENCY_ONLY : CADOnly),
    },
    {
      label: vp.BankAccountTransferLabel.From,
      id: vp.BankAccountTransferId.From,
      require: true,
      preset: 'text',
      type: 'select',
      placeholder: 'Please Select...',
      visible: true,
      allowNewOption: true,
      getSelectOptions: () => BankAccountsOptions,
      disableFiltering: () => true,
      searchCallback: (value: string) => {
        if (value === '') {
          setBankAccountOptions(originalBankList);
        } else {
          handleBankAccountsSearch(value);
        }
      },
      onChangeCallBack: (event) => {
        const Contact = event.target.value;

        const contactBankAccount = Object.values(contactBankAccounts || {}).find(
          (bank) => (bank as vp.ClientBankAccountsLists).Token === Contact,
        ) as vp.ClientBankAccountsLists | undefined;

        onSourceBankChange(contactBankAccount);
      },
      SelectLabel: () => {
        if (!sourceBank) return undefined;

        return `${sourceBank.Currency} ${sourceBank.AccountHolderName} ${getMaskedNumber(sourceBank.AccountNumber)}`;
      },
    },
    {
      label: vp.BankAccountTransferLabel.To,
      id: vp.BankAccountTransferId.To,
      require: true,
      preset: 'text',
      type: 'select',
      placeholder: 'Please Select...',
      visible: true,
      allowNewOption: true,
      getSelectOptions: () => recipientBankAccountOptions,
      disableFiltering: () => true,
      searchCallback: (value: string) => {
        if (value === '') {
          setRecipientBankAccountOptions(originalRecipientBankList);
        } else {
          handleRecipientBankAccountsSearch(value);
        }
      },
      onChangeCallBack: (event) => {
        const Contact = event.target.value;

        const contactBankAccount = Object.values(recipientContactBankAccounts || {}).find(
          (bank) => (bank as vp.ClientBankAccountsLists).Token === Contact,
        ) as vp.ClientBankAccountsLists | undefined;

        onDestBankChange(contactBankAccount);
      },
      SelectLabel: () => {
        if (!destBank) return undefined;

        return `${destBank.Currency} ${destBank.AccountHolderName} ${getMaskedNumber(destBank.AccountNumber)}`;
      },
    },
    {
      label: vp.BankAccountTransferLabel.Amount,
      id: vp.BankAccountTransferId.Amount,
      require: true,
      preset: 'currency',
      type: 'input',
      placeholder: '0.00',
      visible: true,
      getSelectOptions: () => ({}),
    },
  ];

  function buildBankAccountOptions(lists: vp.ClientBankAccountsLists[]) {
    return lists.reduce(
      (accumulator, data) => ({
        ...accumulator,
        [data.Token]: `${data.Currency} ${data.AccountHolderName} ${getMaskedNumber(data.AccountNumber)} `,
      }),
      {},
    );
  }

  function getMaskedNumber(num: string) {
    return num?.slice(-7).replace(/\d(?=\d{4})/g, '*');
  }
};
